import React, { useCallback, useEffect, useState } from "react";
import { Checkbox, CircularProgress, FormControlLabel } from "@material-ui/core";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { getListPartnerService, PutPartnerBulkAdminContentFields, uploadAvatarMutiImage } from "../../../services/PartnerService";
import Partner from "../../../models/Partner";
import useAlertDialog from "../../hooks/useAlertDialog";
import { AlertType } from "../../hooks/common/useAttachAlertDialog";

// ── styles ────────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
	padding: "24px",
	fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
	maxWidth: "1400px",
};

const sectionCard: React.CSSProperties = {
	background: "#F9FAFB",
	border: "1px solid #E5E7EB",
	borderRadius: 10,
	padding: "18px 20px",
	marginBottom: 20,
};

const sectionTitle: React.CSSProperties = {
	fontWeight: 700,
	fontSize: 15,
	marginBottom: 14,
	color: "#1F2937",
};

const labelStyle: React.CSSProperties = {
	display: "block",
	fontWeight: 600,
	fontSize: 13,
	color: "#374151",
	marginBottom: 5,
};

const inputStyle: React.CSSProperties = {
	width: "100%",
	boxSizing: "border-box",
	padding: "9px 12px",
	borderRadius: 6,
	border: "1px solid #D1D5DB",
	fontSize: 14,
	background: "#fff",
	outline: "none",
};

const tableHeaderCell: React.CSSProperties = {
	background: "#F3F4F6",
	color: "#374151",
	fontWeight: 700,
	fontSize: 12,
	padding: "8px 10px",
	textAlign: "left",
	borderBottom: "2px solid #E5E7EB",
	whiteSpace: "nowrap",
};

const tableCell: React.CSSProperties = {
	padding: "7px 10px",
	fontSize: 13,
	color: "#374151",
	borderBottom: "1px solid #F3F4F6",
	verticalAlign: "middle",
};

const primaryBtn: React.CSSProperties = {
	padding: "9px 22px",
	background: "linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)",
	color: "#fff",
	border: "none",
	borderRadius: 8,
	fontWeight: 600,
	fontSize: 14,
	cursor: "pointer",
};

const secondaryBtn: React.CSSProperties = {
	padding: "9px 20px",
	background: "#fff",
	color: "#374151",
	border: "1px solid #D1D5DB",
	borderRadius: 8,
	fontWeight: 600,
	fontSize: 14,
	cursor: "pointer",
};

// ── component ─────────────────────────────────────────────────────────────────

export default function PartnerBulkEditPage() {
	const { openAlertDialog } = useAlertDialog();

	// ── Station list state ─────────────────────────────────────────────────────
	const [stations, setStations] = useState<Partner[]>([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	// ── Content field state ────────────────────────────────────────────────────
	const [updateFaq, setUpdateFaq] = useState(false);
	const [faqJson, setFaqJson] = useState("");
	const [faqJsonError, setFaqJsonError] = useState<string | null>(null);

	const [updateImagesEnabled, setUpdateImagesEnabled] = useState(false);
	const [imagesEnabledValue, setImagesEnabledValue] = useState(true);

	const [updateMeetImages, setUpdateMeetImages] = useState(false);
	const [meetImageFiles, setMeetImageFiles] = useState<File[]>([]);
	const [meetImagePreviews, setMeetImagePreviews] = useState<string[]>([]);
	const [uploadingImages, setUploadingImages] = useState(false);



	// ── Confirm dialog state ───────────────────────────────────────────────────
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	// ── Load stations ──────────────────────────────────────────────────────────
	const loadStations = useCallback(async (name?: string) => {
		setLoading(true);
		try {
			const res = await getListPartnerService(1, 500, name || undefined);
			setStations((res.data as Partner[]).filter((s: Partner) => s.id != null && s.id !== ""));
		} catch {
			setStations([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadStations();
	}, [loadStations]);

	// ── Search ─────────────────────────────────────────────────────────────────
	const handleSearch = () => {
		setSelectedIds([]);
		setSearch(searchInput);
		loadStations(searchInput);
	};

	const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") handleSearch();
	};

	// ── Selection ──────────────────────────────────────────────────────────────
	const allSelected = stations.length > 0 && selectedIds.length === stations.length;

	const toggleAll = (checked: boolean) => {
		if (checked) {
			setSelectedIds(stations.filter((s) => s.id).map((s) => s.id as string));
		} else {
			setSelectedIds([]);
		}
	};

	const toggleOne = (id: string, checked: boolean) => {
		if (checked) {
			setSelectedIds((prev) => [...prev, id]);
		} else {
			setSelectedIds((prev) => prev.filter((i) => i !== id));
		}
	};

	// ── FAQ JSON validation ────────────────────────────────────────────────────
	const isValidFaq = (arr: any[]): boolean =>
		arr.every(
			(item) =>
				typeof item === "object" &&
				item !== null &&
				typeof item.question === "string" &&
				item.question.trim() !== "" &&
				typeof item.answer === "string" &&
				item.answer.trim() !== ""
		);

	const validateFaqJson = (value: string): boolean => {
		if (!value.trim()) {
			setFaqJsonError("FAQ content cannot be empty.");
			return false;
		}
		try {
			const parsed = JSON.parse(value);
			if (!Array.isArray(parsed)) {
				setFaqJsonError("FAQ content must be a JSON array.");
				return false;
			}
			if (!isValidFaq(parsed)) {
				setFaqJsonError("Each FAQ item must have non-empty 'question' and 'answer' string fields.");
				return false;
			}
			setFaqJsonError(null);
			return true;
		} catch {
			setFaqJsonError("Invalid JSON format.");
			return false;
		}
	};

	const onFaqChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setFaqJson(e.target.value);
		if (faqJsonError) validateFaqJson(e.target.value);
	};

	// ── Guard: any field to apply? ─────────────────────────────────────────────
	const hasFieldSelected = updateFaq || updateImagesEnabled || updateMeetImages;

	const canApply =
		selectedIds.length > 0 &&
		hasFieldSelected &&
		!isSaving &&
		!(updateFaq && !!faqJsonError);

	// ── Open confirm ───────────────────────────────────────────────────────────
	const handleApplyClick = () => {
		if (updateFaq && !validateFaqJson(faqJson)) return;
		setConfirmOpen(true);
	};

	// ── Apply ──────────────────────────────────────────────────────────────────
	const handleConfirm = async () => {
		setConfirmOpen(false);
		setIsSaving(true);
		try {
			const payload: Parameters<typeof PutPartnerBulkAdminContentFields>[1] = {};

			if (updateFaq) {
				payload.faqContent = JSON.parse(faqJson);
			}
			if (updateImagesEnabled) {
				payload.imagesEnabled = imagesEnabledValue;
			}
			if (updateMeetImages && meetImageFiles.length > 0) {
				setUploadingImages(true);
				const uploadRes = await uploadAvatarMutiImage(meetImageFiles);
				setUploadingImages(false);
				const items = Array.isArray(uploadRes) ? uploadRes : [];
				const urls = items.map((item: any) => item.url || item.path).filter(Boolean) as string[];
				if (urls.length > 0) {
					payload.meetStationImages = urls;
					payload.imagesEnabled = true;
				}
			}


			await PutPartnerBulkAdminContentFields(selectedIds, payload);

			openAlertDialog?.(
				AlertType.Success,
				`Successfully applied changes to ${selectedIds.length} station(s).`,
				() => {
					setSelectedIds([]);
					setUpdateFaq(false);
					setFaqJson("");
					setUpdateImagesEnabled(false);
					setUpdateMeetImages(false);
					setMeetImageFiles([]);
					setMeetImagePreviews([]);

				},
			);
		} catch (err: any) {
			openAlertDialog?.(AlertType.Fail, err?.response?.data?.message || "An error occurred. Please try again.");
		} finally {
			setIsSaving(false);
		}
	};

	// ── Render ─────────────────────────────────────────────────────────────────
	return (
		<MyBookingWrapper>
			<div style={pageStyle}>
				{/* Page header */}
				<div style={{ marginBottom: 24 }}>
					<h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1a237e" }}>
						Station Bulk Edit — Page V2 Content
					</h2>
					<p style={{ margin: "6px 0 0", fontSize: 14, color: "#6B7280" }}>
						Select stations and apply content fields to all selected stations at once.
					</p>
				</div>

				{/* Step 1: Station selection */}
				<div style={sectionCard}>
					<div style={sectionTitle}>Step 1: Select Stations</div>

					{/* Search bar */}
					<div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
						<input
							style={{ ...inputStyle, flex: 1, maxWidth: 380 }}
							placeholder="Search by name..."
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							onKeyDown={handleSearchKeyDown}
						/>
						<button
							style={secondaryBtn}
							onClick={handleSearch}
							disabled={loading}>
							Search
						</button>
						{search && (
							<button
								style={secondaryBtn}
								onClick={() => {
									setSearchInput("");
									setSearch("");
									setSelectedIds([]);
									loadStations();
								}}>
								Clear
							</button>
						)}
						<div style={{ marginLeft: "auto", fontSize: 14, fontWeight: 700, color: "#1565c0", alignSelf: "center" }}>
							Selected: {selectedIds.length} / {stations.length}
						</div>
					</div>

					{/* Table */}
					<div
						style={{
							overflow: "auto",
							maxHeight: 420,
							borderRadius: 8,
							border: "1px solid #E5E7EB",
							boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
						}}>
						<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
							<thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
								<tr>
									<th style={{ ...tableHeaderCell, width: 44, textAlign: "center" }}>
										<input
											type="checkbox"
											checked={allSelected}
											onChange={(e) => toggleAll(e.target.checked)}
											style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#1a237e" }}
										/>
									</th>
									<th style={tableHeaderCell}>ID</th>
									<th style={tableHeaderCell}>Name</th>
									<th style={tableHeaderCell}>Display Name</th>
									<th style={tableHeaderCell}>Status</th>
									<th style={tableHeaderCell}>Email</th>
								</tr>
							</thead>
							<tbody>
								{loading && (
									<tr>
										<td
											colSpan={6}
											style={{ textAlign: "center", padding: 32 }}>
											<CircularProgress size={28} />
										</td>
									</tr>
								)}
								{!loading && stations.length === 0 && (
									<tr>
										<td
											colSpan={6}
											style={{ textAlign: "center", padding: 32, color: "#9CA3AF" }}>
											No stations found
										</td>
									</tr>
								)}
								{!loading &&
									stations.map((s) => {
										const id = s.id;
										if (!id) return null; // skip null-id rows
										const checked = selectedIds.includes(id);
										return (
											<tr
												key={id}
												style={{
													background: checked ? "#EFF6FF" : undefined,
													cursor: "pointer",
												}}
												onClick={() => toggleOne(id, !checked)}>
												<td style={{ ...tableCell, textAlign: "center" }}>
													<input
														type="checkbox"
														checked={checked}
														onChange={(e) => {
															e.stopPropagation();
															toggleOne(id, e.target.checked);
														}}
														onClick={(e) => e.stopPropagation()}
														style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#1a237e" }}
													/>
												</td>
												<td style={tableCell}>{id}</td>
												<td style={{ ...tableCell, fontWeight: checked ? 600 : undefined }}>{s.name || "-"}</td>
												<td style={tableCell}>{s.displayName || "-"}</td>
												<td style={tableCell}>
													<span
														style={{
															display: "inline-block",
															padding: "2px 8px",
															borderRadius: 99,
															fontSize: 11,
															fontWeight: 600,
															background:
																String(s.status || "").toLowerCase() === "published"
																	? "#D1FAE5"
																	: String(s.status || "").toLowerCase() === "active"
																	? "#DBEAFE"
																	: "#F3F4F6",
															color:
																String(s.status || "").toLowerCase() === "published"
																	? "#065F46"
																	: String(s.status || "").toLowerCase() === "active"
																	? "#1E40AF"
																	: "#6B7280",
														}}>
														{s.status || "unknown"}
													</span>
												</td>
												<td style={{ ...tableCell, color: "#6B7280" }}>{s.email || "-"}</td>
											</tr>
										);
									})}
							</tbody>
						</table>
					</div>
				</div>

				{/* Step 2: Fields to apply */}
				<div style={sectionCard}>
					<div style={sectionTitle}>Step 2: Fields to Apply</div>
					<p style={{ margin: "0 0 16px", fontSize: 13, color: "#6B7280" }}>
						Enable a checkbox to include that field in the bulk update. Only enabled fields will be changed.
					</p>

					<div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
						{/* FAQ Content */}
						<div>
							<FormControlLabel
								style={{ margin: 0 }}
								control={
									<Checkbox
										color="primary"
										checked={updateFaq}
										onChange={(e) => {
											setUpdateFaq(e.target.checked);
											if (!e.target.checked) setFaqJsonError(null);
										}}
									/>
								}
								label={
									<span style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>
										Update FAQ Content
									</span>
								}
							/>
							{updateFaq && (
								<div style={{ marginTop: 10 }}>
									<label style={labelStyle}>
										FAQ Content (JSON array of{" "}
										<code
											style={{
												fontSize: 12,
												background: "#F3F4F6",
												padding: "1px 5px",
												borderRadius: 4,
											}}>
											{`{"question":"...","answer":"..."}`}
										</code>
										)
									</label>
									<textarea
										rows={5}
										placeholder={`[\n  {"question": "What are your hours?", "answer": "We are open 24/7."}\n]`}
										value={faqJson}
										onChange={onFaqChange}
										style={{
											...inputStyle,
											fontFamily: "monospace",
											fontSize: 13,
											resize: "vertical",
										}}
									/>
									{faqJsonError && (
										<div style={{ marginTop: 4, fontSize: 12, color: "#DC2626", fontWeight: 600 }}>
											{faqJsonError}
										</div>
									)}
								</div>
							)}
						</div>

						{/* Meet Station Images */}
						<div>
							<FormControlLabel
								style={{ margin: 0 }}
								control={
									<Checkbox
										color="primary"
										checked={updateMeetImages}
										onChange={(e) => {
											setUpdateMeetImages(e.target.checked);
											if (!e.target.checked) {
												setMeetImageFiles([]);
												setMeetImagePreviews([]);
											}
										}}
									/>
								}
								label={
									<span style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>
										Update Meet Station Images
									</span>
								}
							/>
							{updateMeetImages && (
								<div style={{ marginTop: 10 }}>
									<label style={labelStyle}>Select images for "Meet your Station" tab</label>
									<input
										type="file"
										multiple
										accept="image/*"
										onChange={(e) => {
											const files = Array.from(e.target.files || []);
											setMeetImageFiles(files);
											setMeetImagePreviews(files.map((f) => URL.createObjectURL(f)));
										}}
										style={{ fontSize: 13 }}
									/>
									{meetImagePreviews.length > 0 && (
										<div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
											{meetImagePreviews.map((url, i) => (
												<img
													key={i}
													src={url}
													alt={`Preview ${i + 1}`}
													style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6, border: "1px solid #D1D5DB" }}
												/>
											))}
										</div>
									)}
									{uploadingImages && (
										<div style={{ marginTop: 8, fontSize: 13, color: "#1565c0" }}>
											Uploading images...
										</div>
									)}
								</div>
							)}
						</div>

						{/* Station Landing Toggle */}
						<div>
							<FormControlLabel
								style={{ margin: 0 }}
								control={
									<Checkbox
										color="primary"
										checked={updateImagesEnabled}
										onChange={(e) => setUpdateImagesEnabled(e.target.checked)}
									/>
								}
								label={
									<span style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>
										Toggle Station Landing Section
									</span>
								}
							/>
							{updateImagesEnabled && (
								<div style={{ marginTop: 10, marginLeft: 32 }}>
									<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
										<button
											style={{
												padding: "6px 16px",
												borderRadius: 6,
												border: "1px solid",
												fontWeight: 600,
												fontSize: 13,
												cursor: "pointer",
												background: imagesEnabledValue ? "#D1FAE5" : "#fff",
												color: imagesEnabledValue ? "#065F46" : "#374151",
												borderColor: imagesEnabledValue ? "#10B981" : "#D1D5DB",
											}}
											onClick={() => setImagesEnabledValue(true)}>
											Enable
										</button>
										<button
											style={{
												padding: "6px 16px",
												borderRadius: 6,
												border: "1px solid",
												fontWeight: 600,
												fontSize: 13,
												cursor: "pointer",
												background: !imagesEnabledValue ? "#FEE2E2" : "#fff",
												color: !imagesEnabledValue ? "#991B1B" : "#374151",
												borderColor: !imagesEnabledValue ? "#EF4444" : "#D1D5DB",
											}}
											onClick={() => setImagesEnabledValue(false)}>
											Disable
										</button>
									</div>
									<p style={{ margin: "6px 0 0", fontSize: 12, color: "#6B7280" }}>
										{imagesEnabledValue
											? "Station Landing section will be shown on selected stations."
											: "Station Landing section will be hidden on selected stations."}
									</p>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Step 3: Apply */}
				<div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
					<button
						style={{
							...primaryBtn,
							opacity: canApply ? 1 : 0.5,
							cursor: canApply ? "pointer" : "not-allowed",
						}}
						disabled={!canApply}
						onClick={handleApplyClick}>
						{isSaving ? "Applying..." : `Apply to ${selectedIds.length} Station(s)`}
					</button>
					{!hasFieldSelected && (
						<span style={{ fontSize: 13, color: "#9CA3AF" }}>
							Enable at least one field to apply.
						</span>
					)}
					{selectedIds.length === 0 && hasFieldSelected && (
						<span style={{ fontSize: 13, color: "#9CA3AF" }}>
							Select at least one station.
						</span>
					)}
				</div>

				{/* Confirm dialog (inline modal) */}
				{confirmOpen && (
					<div
						style={{
							position: "fixed",
							inset: 0,
							background: "rgba(0,0,0,0.45)",
							zIndex: 1300,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
						onClick={() => setConfirmOpen(false)}>
						<div
							style={{
								background: "#fff",
								borderRadius: 12,
								padding: "28px 32px",
								maxWidth: 440,
								width: "90%",
								boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
							}}
							onClick={(e) => e.stopPropagation()}>
							<h3 style={{ margin: "0 0 12px", fontSize: 18, color: "#1F2937" }}>Confirm Bulk Update</h3>
							<p style={{ margin: "0 0 8px", fontSize: 14, color: "#374151" }}>
								You are about to apply changes to{" "}
								<strong style={{ color: "#1a237e" }}>{selectedIds.length}</strong> station(s).
							</p>
							<ul style={{ margin: "0 0 20px", paddingLeft: 20, fontSize: 13, color: "#4B5563" }}>
								{updateFaq && <li>FAQ Content (JSON)</li>}
								{updateMeetImages && meetImageFiles.length > 0 && <li>Meet Station Images ({meetImageFiles.length} file(s))</li>}
								{updateImagesEnabled && (
									<li>Station Landing: {imagesEnabledValue ? "Enable" : "Disable"}</li>
								)}
							</ul>
							<p style={{ margin: "0 0 20px", fontSize: 13, color: "#6B7280" }}>
								This action cannot be undone automatically. Are you sure?
							</p>
							<div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
								<button
									style={secondaryBtn}
									onClick={() => setConfirmOpen(false)}>
									Cancel
								</button>
								<button
									style={primaryBtn}
									onClick={handleConfirm}>
									Confirm & Apply
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</MyBookingWrapper>
	);
}
